// The break-glass pages: asking for a second factor to be removed, cancelling
// somebody else's request, and completing your own once the wait is over.
//
// Three screens in one file because they are one journey and the wording has
// to agree across them — the whole design rests on a person understanding, from
// an email, that something is about to happen to their account and that they
// can stop it. Splitting them into three files is how two of them end up
// describing the delay differently.
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ApiError, api } from '../api';
import { AuthCard, Banner, Field } from '../ui';
import { formatMomentAu } from '../lib/dates';

/** Step one: ask. Answers the same way whether or not the account exists. */
export function LostTwoFactorPage() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      await api.requestTwofaReset(email);
      setSent(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Something went wrong. Try again.');
    } finally {
      setBusy(false);
    }
  }

  if (sent) {
    return (
      <AuthCard title="Check your email">
        <p className="mb-4 text-sm text-gray-500">
          If that address has an account with two-factor authentication, we have emailed it,
          and every administrator at that company, about the request.
        </p>
        <p className="mb-4 text-sm text-gray-500">
          Nothing changes for three days. That wait is deliberate: it is what stops somebody
          who has got into your email from taking the account straight away. If you did not
          ask for this, use the cancel link in that email.
        </p>
        <p className="mb-4 text-sm text-gray-500">
          <strong>Try your recovery codes first.</strong> If you still have one, signing in
          with it is instant and cancels this automatically. If somebody else at your company
          is an administrator, they can also clear it for you in seconds.
        </p>
        <p className="footnote">
          <Link to="/login">Back to sign in</Link>
        </p>
      </AuthCard>
    );
  }

  return (
    <AuthCard title="Lost your phone and your codes?">
      {error && <Banner kind="error">{error}</Banner>}
      <p className="mb-4 text-sm text-gray-500">
        We can remove the two-factor authentication from your account so you can set it up
        again, but not straight away. It takes three days, and everyone who administers your
        company is told about it.
      </p>
      <form
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        <Field
          label="Your email address"
          name="email"
          type="email"
          value={email}
          onChange={setEmail}
          autoComplete="email"
          autoCapitalize="none"
          spellCheck={false}
          enterKeyHint="go"
        />
        <button className="btn-primary full" type="submit" disabled={busy}>
          {busy ? 'Sending…' : 'Start the three-day wait'}
        </button>
      </form>
      <p className="footnote">
        <Link to="/login">I can still sign in</Link>
      </p>
    </AuthCard>
  );
}

/**
 * The cancel link from the email.
 *
 * Acts on load rather than asking again. Somebody clicking this has just read
 * "somebody is trying to remove the security from your account" — putting a
 * confirmation button in the way would be one more thing between them and
 * stopping it, for no benefit: cancelling is the safe direction, and a request
 * cancelled by mistake can simply be made again.
 */
export function CancelTwoFactorResetPage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const [state, setState] = useState<'working' | 'done' | 'failed'>('working');

  useEffect(() => {
    if (!token) {
      setState('failed');
      return;
    }
    void api
      .cancelTwofaReset(token)
      .then(() => setState('done'))
      .catch(() => setState('failed'));
  }, [token]);

  if (state === 'working') {
    return (
      <AuthCard title="Cancelling…">
        <p className="text-sm text-gray-500">One moment.</p>
      </AuthCard>
    );
  }

  if (state === 'failed') {
    return (
      <AuthCard title="That link did not work">
        <Banner kind="error">
          The link is incomplete or has expired. If you are worried about the account, change
          its password; that is the fastest way to shut somebody out.
        </Banner>
        <p className="footnote">
          <Link to="/forgot-password">Reset the password</Link>
        </p>
      </AuthCard>
    );
  }

  return (
    <AuthCard title="Cancelled">
      <Banner kind="ok">
        The request to remove two-factor authentication has been stopped. Nothing changed.
      </Banner>
      <p className="mb-4 text-sm text-gray-500">
        If you did not make that request, somebody else knows the email address on this
        account and is trying to get in. Change the password now.
      </p>
      <p className="footnote">
        <Link to="/forgot-password">Reset the password</Link>
      </p>
      <p className="footnote">
        <Link to="/login">Back to sign in</Link>
      </p>
    </AuthCard>
  );
}

/** The "it is ready now" link, which also politely refuses when it is not. */
export function CompleteTwoFactorResetPage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const [state, setState] = useState<'idle' | 'busy' | 'done' | 'waiting' | 'failed'>('idle');
  const [readyAt, setReadyAt] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    setState('busy');
    setError(null);
    try {
      const result = await api.confirmTwofaReset(token);
      if (result.completed) {
        setState('done');
      } else {
        setReadyAt(result.readyAt ?? null);
        setState('waiting');
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Something went wrong. Try again.');
      setState('failed');
    }
  }

  if (state === 'done') {
    return (
      <AuthCard title="Two-factor removed">
        <Banner kind="ok">
          Your account no longer asks for a code. Everywhere that was signed in has been
          signed out.
        </Banner>
        <p className="mb-4 text-sm text-gray-500">
          Set a new password, then sign in and set up your authenticator app again. If you
          administer the company you will be asked to do that straight away.
        </p>
        <p className="footnote">
          <Link to="/forgot-password">Set a new password</Link>
        </p>
      </AuthCard>
    );
  }

  if (state === 'waiting') {
    return (
      <AuthCard title="Not yet">
        <p className="mb-4 text-sm text-gray-500">
          The three-day wait has not finished. Nothing has been changed and your request is
          still standing; come back after{' '}
          <strong>
            {readyAt
              ? formatMomentAu(readyAt)
              : 'the time in your email'}
          </strong>
          .
        </p>
        <p className="footnote">
          <Link to="/login">Back to sign in</Link>
        </p>
      </AuthCard>
    );
  }

  return (
    <AuthCard title="Remove two-factor authentication">
      {error && <Banner kind="error">{error}</Banner>}
      <p className="mb-4 text-sm text-gray-500">
        This removes the authenticator app and the recovery codes from your account, and
        signs out everywhere. You will then set a new password and start again.
      </p>
      <button
        className="btn-primary full"
        type="button"
        disabled={state === 'busy' || token === ''}
        onClick={() => void submit()}
      >
        {state === 'busy' ? 'Working…' : 'Remove it'}
      </button>
      <p className="footnote">
        <Link to="/login">Cancel, I can still sign in</Link>
      </p>
    </AuthCard>
  );
}
